import './ManageWorkspaceDrawer.less'

import { ButtonDanger, DrawerItemHint } from 'front-end/src/components'
import Tooltip from 'front-end/src/components/common/tooltip/Tooltip'
import {
  CampaignModel,
  OrganisationRoleEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { usePublicApiSession } from 'front-end/src/public-api/hooks/usePublicApiSession'
import React, { FC, useEffect, useState } from 'react'

import { t, Trans } from '@lingui/macro'

import { IWorkspace } from '../../../../../types/Workspace'
import httpApi from '../../../api/http-api'
import Input from '../../../components/common/form/input/Input'
import { Upload } from '../../../components/common/form/upload/Upload'
import { notification } from '../../../components/common/notifications/Notifications'
import { Drawer } from '../../../components/common/page/drawer/Drawer'
import DrawerImage from '../../../components/common/page/drawer/DrawerImage'
import DrawerItem from '../../../components/common/page/drawer/DrawerItem'
import { DeleteWorkspaceModal } from '../DeleteWorkspaceModal'
import { WorkspaceBasicInfoQuery } from '../ManageWorkspacePage.codegen'
import { useManageWorkspaceDrawerRelatedCampaignsLazyQuery } from './ManageWorkspaceDrawer.codegen'
import TagSelect, {
  TAG_TARGET_TYPES
} from 'front-end/src/components/common/tag/TagSelect'
import IconTag from 'front-end/src/components/common/icon/IconTag'
import { useCachedWorkspacesQuery } from 'front-end/src/app/stores/useCachedWorkspacesQuery'

const patchWorkspace = async (
  workspaceId: number,
  patch: Partial<IWorkspace>
) => {
  try {
    await httpApi.workspace.update(workspaceId, patch)
  } catch (e) {
    console.error(e)
    notification.error(e.message)
  }
}

export const ManageWorkspaceDrawer: FC<{
  show?: boolean
  onHide: () => any
  workspace: WorkspaceBasicInfoQuery['workspace']
  onUpdate: () => any
}> = (props) => {
  const { refetch } = useCachedWorkspacesQuery()
  const [state, setState] = useState<{
    deleteModalVisible: boolean
    name: string | null | undefined
    description: string
    isUploadingCoverImage: boolean
    isDeletingCoverImage: boolean
    relatedActiveCampaigns: Partial<CampaignModel>[]
  }>({
    deleteModalVisible: false,
    name: props.workspace?.name,
    description: props.workspace?.description || '',
    isUploadingCoverImage: false,
    isDeletingCoverImage: false,
    relatedActiveCampaigns: []
  })

  const [getRelatedCampaigns, { data }] =
    useManageWorkspaceDrawerRelatedCampaignsLazyQuery()
  const { session } = usePublicApiSession()
  useEffect(() => {
    if (
      data?.workspace?.__typename === 'WorkspaceModelAsCoordinator' &&
      data?.workspace.relatedCampaigns
    ) {
      setState({
        ...state,
        deleteModalVisible: true,
        relatedActiveCampaigns: data.workspace.relatedCampaigns
      })
    }
  }, [data?.workspace.relatedCampaigns])
  const { workspace, onUpdate } = props
  if (!workspace) {
    return null
  }

  const getWorkspaceImageThumbnailUrl = () => {
    if (workspace) {
      if (
        workspace &&
        workspace.coverImage &&
        workspace.coverImage.imageVariants &&
        workspace.coverImage.imageVariants['300x200']
      ) {
        return workspace.coverImage.imageVariants['300x200'].cdnUrl
      } else if (workspace.coverImage && workspace.coverImage.cdnUrl) {
        return workspace.coverImage.cdnUrl
      } else {
        return null
      }
    }
  }

  const canDelete =
    session?.user.organisation_role !== OrganisationRoleEnum.ADMIN
  return (
    <>
      <Drawer
        className="ManageWorkspaceDrawer"
        title={t`Settings`}
        show={props.show}
        onHide={props.onHide}
      >
        <DrawerItem label={t`Name`}>
          <Input
            title="name"
            light
            value={state.name as string}
            debouncedOnChange={async (value: string) => {
              setState({ ...state, name: value })
              await patchWorkspace(workspace.id, {
                name: value.trim()
              })
              onUpdate()
            }}
          />
        </DrawerItem>
        <DrawerItem label={t`Cover image`} className="full-width">
          <DrawerImage imageUrl={getWorkspaceImageThumbnailUrl()} large>
            <ButtonDanger
              onClick={() => {
                setState({ ...state, isDeletingCoverImage: true })
                httpApi.workspace
                  .removeCoverImage(workspace.id)
                  .then(async () => {
                    onUpdate()
                  })
                  .catch((err) => {
                    console.error(err)
                    notification.error(t`Unable to remove cover image`)
                  })

                  .finally(() => {
                    setState({
                      ...state,
                      isDeletingCoverImage: false
                    })
                  })
              }}
            >
              <Trans>Remove image</Trans>
            </ButtonDanger>
          </DrawerImage>
          <Upload
            title={t`Choose image`}
            accept="image/png, image/jpeg, image/gif"
            isLoading={state.isUploadingCoverImage}
            onChange={(e) => {
              const files = e.currentTarget.files
              if (files && files.length) {
                const file = files[0]
                setState({
                  ...state,
                  isUploadingCoverImage: true
                })
                httpApi.workspace
                  .uploadCoverImage(workspace.id, file)
                  .then(async () => {
                    onUpdate()
                  })
                  .catch((err) => {
                    console.error(err)
                    notification.error(t`Unable to upload cover image`)
                  })

                  .finally(() => {
                    setState({
                      ...state,
                      isUploadingCoverImage: false
                    })
                  })
              }
            }}
          />
        </DrawerItem>
        <DrawerItem
          label={
            <>
              <IconTag />
              <Trans>Category tags</Trans>
            </>
          }
          className="full-width"
        >
          <TagSelect
            onChange={refetch}
            defaultValue={workspace.tags}
            target={{
              type: TAG_TARGET_TYPES.workspace,
              id: workspace.id
            }}
          />
          <DrawerItemHint>
            <Trans>
              Use this for categorisation of Workspaces on the Homepage
            </Trans>
          </DrawerItemHint>
        </DrawerItem>
        <DrawerItem label={t`Delete this workspace`}>
          <Tooltip
            text={
              !props.show || !canDelete
                ? null
                : t`Only administrators can delete workspaces`
            }
          >
            <ButtonDanger
              isDisabled={canDelete}
              onClick={async () => {
                getRelatedCampaigns({
                  variables: {
                    workspaceId: workspace.id
                  }
                })
                setState({
                  ...state,
                  deleteModalVisible: true
                })
              }}
            >
              <Trans>Delete</Trans>
            </ButtonDanger>
          </Tooltip>
        </DrawerItem>
      </Drawer>

      <DeleteWorkspaceModal
        workspaceId={workspace.id}
        deleteModalVisible={state.deleteModalVisible}
        setDeletedModalVisible={(value: boolean) =>
          setState({
            ...state,
            deleteModalVisible: value
          })
        }
        // @ts-expect-error 9/11/2020
        relatedActiveCampaigns={state.relatedActiveCampaigns}
        resetRelatedActiveCampaigns={() =>
          setState({
            ...state,

            relatedActiveCampaigns: []
          })
        }
      />
    </>
  )
}
